import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Search, ChevronRight, Building2, KeyRound, Users as UsersIcon, Hammer, Calendar,
} from "lucide-react";
import UserManageDrawer from "@/components/UserManageDrawer";

const ROLE_BADGE = {
  owner:      { label: "OWNER",      cls: "bg-black text-safety-yellow border-safety-yellow" },
  admin:      { label: "ADMIN",      cls: "bg-safety-yellow text-black border-black" },
  supervisor: { label: "SUPERVISOR", cls: "bg-blue-600 text-white border-blue-600" },
  employee:   { label: "EMPLOYEE",   cls: "bg-muted text-foreground border-foreground" },
};

export default function OwnerCompanyUsers() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [co, us, all] = await Promise.all([
        api.get(`/owner/companies/${companyId}`),
        api.get(`/owner/companies/${companyId}/users`),
        api.get("/owner/companies"),
      ]);
      setCompany(co.data);
      setUsers(us.data);
      setCompanies(all.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      navigate("/owner/users?tab=companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [companyId]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(needle) ||
      (u.email || "").toLowerCase().includes(needle) ||
      (u.role || "").toLowerCase().includes(needle)
    );
  }, [users, q]);

  const onUserChanged = (updated) => {
    if (updated == null) {
      // deleted
      setUsers((prev) => prev.filter((u) => u.id !== selected.id));
      setSelected(null);
      return;
    }
    // If the user was moved to a different company, drop them from this view.
    if (updated.company_id !== companyId) {
      setUsers((prev) => prev.filter((u) => u.id !== updated.id));
      setSelected(null);
      toast.info("User moved to another company");
      return;
    }
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
    setSelected(updated);
  };

  if (loading) {
    return <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Loading…</div>;
  }
  if (!company) return null;

  return (
    <div className="p-4 md:p-10 max-w-7xl mx-auto">
      {/* Breadcrumb */}
      <nav data-testid="breadcrumb" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
        <Link to="/owner/users?tab=companies" className="hover:text-safety-yellow inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" strokeWidth={3} /> Companies
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground truncate">{company.name}</span>
      </nav>

      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 border-2 border-safety-yellow bg-safety-yellow/15 px-3 py-1 mb-3 text-[10px] font-bold uppercase tracking-[0.25em]">
            <Building2 className="w-3 h-3" strokeWidth={3} />
            Company Drill-Down
          </div>
          <h1 className="font-heading text-3xl md:text-4xl truncate">{company.name}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Manage users belonging to this tenant.
          </p>
        </div>
      </div>

      {/* Meta tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        <MetaTile icon={Hammer}      label="Trade"        value={company.trade || "—"} />
        <MetaTile icon={KeyRound}    label="Access Code"  value={company.access_code} mono />
        <MetaTile icon={UsersIcon}   label="Users"        value={company.user_count} />
        <MetaTile icon={Calendar}    label="Onboarded"    value={company.created_at ? new Date(company.created_at).toLocaleDateString() : "—"} />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          data-testid="drill-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email, or role…"
          className="industrial-input pl-10"
        />
      </div>

      {/* Users table */}
      {rows.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider border-2 border-foreground/30 dark:border-safety-yellow/30">
          No users in this company.
        </div>
      ) : (
        <div className="border-2 border-foreground dark:border-safety-yellow overflow-x-auto bg-card">
          <table className="w-full text-sm" data-testid="drill-users-table">
            <thead>
              <tr className="bg-foreground/5 dark:bg-safety-yellow/10 border-b-2 border-foreground dark:border-safety-yellow">
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Name</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Email</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Role</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Job Title</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">Status</th>
                <th className="px-2 py-3" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const r = ROLE_BADGE[u.role] || ROLE_BADGE.employee;
                const active = u.active !== false;
                return (
                  <tr
                    key={u.id}
                    role="button"
                    tabIndex={0}
                    data-testid={`drill-user-row-${u.id}`}
                    onClick={() => setSelected(u)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(u); } }}
                    className="border-b-2 border-foreground/10 dark:border-safety-yellow/20 last:border-b-0 cursor-pointer hover:bg-safety-yellow/10 transition-colors"
                  >
                    <td className="px-4 py-3 font-bold">{u.name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 text-[10px] font-black uppercase tracking-wider border-2 ${r.cls}`}>
                        {r.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{u.job_title || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-1 text-[10px] font-black uppercase tracking-wider border-2 ${
                          active
                            ? "bg-status-current text-white border-status-current"
                            : "bg-status-expired text-white border-status-expired"
                        }`}
                      >
                        {active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-2 py-3 text-muted-foreground"><ChevronRight className="w-4 h-4" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <UserManageDrawer
          user={selected}
          companies={companies}
          currentOwnerId={me?.id}
          onClose={() => setSelected(null)}
          onChanged={onUserChanged}
        />
      )}
    </div>
  );
}

function MetaTile({ icon: Icon, label, value, mono }) {
  return (
    <div className="border-2 border-foreground dark:border-safety-yellow p-3 bg-card">
      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground mb-1">
        <Icon className="w-3 h-3" strokeWidth={3} /> {label}
      </div>
      <div className={`text-base font-bold truncate ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
